import { useCallback, useState, useMemo } from "react";
import { SpacePermission } from "@thenounproject/lingo-core";

import { Inspectable } from "@constants/Inspector";
import useGenerateFilecut from "@actions/useGenerateFilecut";
import useNotifications from "@actions/useNotifications";
import useNavPoint from "@hooks/useNavPoint";
import { useSelectSpace } from "@selectors/entities/spaces";
import { useDownloadErrorHandler } from "./useDownloadErrorHandler";
import { IconStatus } from "../types";

export type CopyOptions = {
  /** File type for conversion (e.g., "PNG", "SVG"). If not provided, copies original type. */
  fileType?: string;
  /** Scale dimensions (e.g., "1x", "2x"). Defaults to "1x". */
  dimensions?: string;
  /** DPI for raster formats */
  dpi?: number;
};

export type CopyState = {
  status: IconStatus;
  fileType?: string;
};

/**
 * Unified hook for copying assets to clipboard.
 * Handles Safari-specific clipboard requirements and SVG vs PNG differences.
 */
export function useAssetCopy() {
  const [copyState, setCopyState] = useState<CopyState>({ status: null });
  const [generateFilecut] = useGenerateFilecut();
  const { showNotification } = useNotifications();
  const { handleDownloadError } = useDownloadErrorHandler();
  const { portalId } = useNavPoint();
  const space = useSelectSpace();

  const isAdmin = useMemo(
    () => space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true,
    [space?.access?.permissions]
  );

  const isSafari = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.navigator.userAgent.includes("Safari") &&
      !window.navigator.userAgent.includes("Chrome"),
    []
  );

  /**
   * Copy a single asset to clipboard with optional conversion.
   * Handles Safari vs non-Safari clipboard APIs and SVG vs PNG blob types.
   */
  const copySingle = useCallback(
    async (inspectable: Inspectable, options?: CopyOptions) => {
      const { asset } = inspectable;
      if (!asset) return { success: false };

      const fileType = options?.fileType || asset.type,
        dimensions = options?.dimensions || "1x",
        dpi = options?.dpi;

      const assetName = asset.name || "Unnamed asset",
        isSvg = fileType.toUpperCase() === "SVG",
        label = `${assetName}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} ${fileType}`;

      setCopyState({ status: "loading", fileType });
      showNotification({ message: `Preparing ${label}...` });

      if (isSafari) {
        // Safari requires clipboard.write() to be called synchronously within
        // the user gesture. We pass a promise for the blob content which resolves
        // after the filecut is generated and fetched.
        const filecutPromise = generateFilecut({
          inspectable,
          fileType,
          dimensions,
          dpi,
          portalId,
          isAdmin,
          userAction: "copy",
        });

        try {
          const type = isSvg ? "text/plain" : "image/png";
          const clipboardItem = new window.ClipboardItem({
            [type]: filecutPromise
              .then(action => {
                if (action.error) {
                  throw new Error(action.error.message || "Failed to generate file");
                }
                return fetch(action.response.result.url);
              })
              .then(response =>
                isSvg
                  ? response.text().then(text => new Blob([text], { type: "text/plain" }))
                  : response.blob()
              ),
          });

          // Critical: call write() synchronously within the user gesture
          await navigator.clipboard.write([clipboardItem]);

          // Check for API errors after clipboard operation
          const action = await filecutPromise;
          if (action.error) {
            const handled = handleDownloadError(action.error, {
              onRetry: () => copySingle(inspectable, options),
            });
            if (handled) return { success: false, error: action.error };
          }

          setCopyState({ status: "success", fileType });
          showNotification({
            message: `${label} has been copied to your clipboard`,
            level: "info",
          });
          setTimeout(() => setCopyState({ status: null }), 3000);
          return { success: true };
        } catch (err) {
          console.error("Safari clipboard error:", err);
          setCopyState({ status: "error", fileType });
          showNotification({
            message: `There was a problem copying "${assetName}" to your clipboard`,
            level: "error",
          });
          setTimeout(() => setCopyState({ status: null }), 3000);
          return { success: false };
        }
      }

      // Non-Safari flow
      const action = await generateFilecut({
        inspectable,
        fileType,
        dimensions,
        dpi,
        portalId,
        isAdmin,
        userAction: "copy",
      });

      if (action.error) {
        const handled = handleDownloadError(action.error, {
          onRetry: () => copySingle(inspectable, options),
        });

        if (!handled) {
          showNotification({
            message:
              action.error.message || "There was a problem copying the asset. Please try again.",
            level: "error",
          });
        }

        setCopyState({ status: "error", fileType });
        setTimeout(() => setCopyState({ status: null }), 3000);
        return { success: false, error: action.error };
      }

      try {
        const fileUrl = action.response.result.url;
        const response = await fetch(fileUrl);

        if (isSvg) {
          const svgText = await response.text();
          await navigator.clipboard.writeText(svgText);
        } else {
          const blob = await response.blob();
          const clipboardItem = new window.ClipboardItem({ "image/png": blob });
          await navigator.clipboard.write([clipboardItem]);
        }

        setCopyState({ status: "success", fileType });
        showNotification({ message: `${label} has been copied to your clipboard`, level: "info" });
        setTimeout(() => setCopyState({ status: null }), 3000);
        return { success: true };
      } catch (err) {
        console.error("Clipboard operation failed:", err);
        showNotification({
          message: `There was a problem copying "${assetName}" to your clipboard`,
          level: "error",
        });
        setCopyState({ status: "error", fileType });
        setTimeout(() => setCopyState({ status: null }), 3000);
        return { success: false };
      }
    },
    [generateFilecut, portalId, isAdmin, isSafari, handleDownloadError, showNotification]
  );

  return useMemo(
    () => ({
      /** Copy single asset to clipboard with optional conversion */
      copySingle,
      /** Current copy state */
      copyState,
      /** Whether a copy is in progress */
      isCopying: copyState.status === "loading",
    }),
    [copySingle, copyState]
  );
}
